import { type Area } from "@/services/backend/buildings/types/area";
import {
  type Building,
  type BuildingOrderBy,
} from "@/services/backend/buildings/types/building";
import { type Level } from "@/services/backend/buildings/types/level";
import { LocationType } from "@/services/backend/buildings/types/location-type";
import { OrdrBy } from "@/shared/lib/ordr/ordrBy";

/** Stands for "ohne Ebene" wherever a level id is expected. */
export const noLevelKey = "__NO_LEVEL__";

export interface AreaFilter {
  search: string;
  /** undefined shows both inside and outside areas. */
  type?: LocationType;
  /** undefined shows every level; noLevelKey shows only areas without a level. */
  levelKey?: string;
  orderBy: OrdrBy<BuildingOrderBy>[];
}

export type AreaRow =
  | { kind: "group"; key: string; levelId?: string }
  | { kind: "area"; area: Area };

export function levelsOfType(building: Building, type?: LocationType): Level[] {
  const levels =
    type === undefined
      ? building.levels
      : building.levels.filter((level) => level.type === type);
  return [...levels].sort((left, right) => left.name.localeCompare(right.name));
}

export function findLevel(
  building: Building,
  levelId?: string | null,
): Level | undefined {
  return levelId
    ? building.levels.find((level) => level.id === levelId)
    : undefined;
}

export function areaCountOfLevel(building: Building, levelId: string): number {
  return building.areas.filter((area) => area.levelId === levelId).length;
}

export function filterAreas(building: Building, filter: AreaFilter): Area[] {
  const search = filter.search.trim().toLocaleLowerCase();

  return building.areas.filter((area) => {
    if (filter.type !== undefined && area.type !== filter.type) {
      return false;
    }
    if (filter.levelKey === noLevelKey && area.levelId) {
      return false;
    }
    if (
      filter.levelKey !== undefined &&
      filter.levelKey !== noLevelKey &&
      area.levelId !== filter.levelKey
    ) {
      return false;
    }
    if (search === "") {
      return true;
    }

    const levelName = findLevel(building, area.levelId)?.name ?? "";
    return (
      area.name.toLocaleLowerCase().includes(search) ||
      levelName.toLocaleLowerCase().includes(search)
    );
  });
}

export function sortAreas(
  areas: Area[],
  orderBy: OrdrBy<BuildingOrderBy>[],
): Area[] {
  const sorted = [...areas];
  const [primary] = orderBy;
  if (!primary) {
    return sorted;
  }

  sorted.sort((left, right) => {
    const comparison =
      primary.field === "name"
        ? left.name.localeCompare(right.name)
        : left[primary.field].localeCompare(right[primary.field]);
    return primary.dsc ? -comparison : comparison;
  });
  return sorted;
}

/**
 * Groups areas under the level they sit on, keeping the order in which the
 * levels first appear in the given areas.
 */
export function groupAreasByLevel(areas: Area[]): AreaRow[] {
  const areasByLevel = new Map<string, Area[]>();

  for (const area of areas) {
    const key = area.levelId ?? noLevelKey;
    const group = areasByLevel.get(key);
    if (group) {
      group.push(area);
    } else {
      areasByLevel.set(key, [area]);
    }
  }

  return [...areasByLevel.entries()].flatMap(([key, group]) => [
    {
      kind: "group" as const,
      key: `group-${key}`,
      levelId: key === noLevelKey ? undefined : key,
    },
    ...group.map((area) => ({ kind: "area" as const, area })),
  ]);
}
